import { useParams, useNavigate } from 'react-router-dom';
import { useMemo, useEffect, useState } from 'react';
import { products } from '@/mocks/products';
import type { Product } from '@/mocks/products';
import { supabase } from '@/lib/supabase';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/base/SeoHead';
import ProductDetailContent from './components/ProductDetailContent';
import RelatedProducts from './components/RelatedProducts';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [dbPrices, setDbPrices] = useState<{
    price_vip: number;
    price_partner: number;
    box_price_partner: number;
    box_quantity: number;
    is_active: boolean;
  } | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  // DB에서 실시간 가격 가져오기 (id로 매칭)
  useEffect(() => {
    if (!id) return;
    const fetchDbPrice = async () => {
      const { data } = await supabase
        .from('products')
        .select('price_vip, price_partner, box_price_partner, box_quantity, is_active')
        .eq('id', id)
        .maybeSingle();
      if (data) setDbPrices(data);
    };
    fetchDbPrice();
  }, [id]);

  const mockProduct = useMemo(
    () => products.find(p => p.id === id),
    [id]
  );

  // DB 가격이 있으면 mock 가격을 DB 가격으로 오버라이드
  // - unitPrice: DB price_vip (원가) → admin은 그대로, partner는 applyMargin(×1.1) 적용
  // - boxPrice: DB box_price_partner (파트너 박스가) → admin은 price_vip×boxQty, partner는 box_price_partner
  const product: Product | undefined = useMemo(() => {
    if (!mockProduct) return undefined;
    if (!dbPrices) return mockProduct;
    return {
      ...mockProduct,
      unitPrice: dbPrices.price_vip > 0 ? Number(dbPrices.price_vip) : mockProduct.unitPrice,
      boxPrice: dbPrices.box_price_partner > 0 ? Number(dbPrices.box_price_partner) : mockProduct.boxPrice,
      boxQuantity: dbPrices.box_quantity > 0 ? dbPrices.box_quantity : mockProduct.boxQuantity,
    };
  }, [mockProduct, dbPrices]);

  const related = useMemo(
    () => product ? products.filter(p => p.category === product.category && p.id !== product.id) : [],
    [product]
  );

  const productJsonLd = useMemo(() => {
    if (!product) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.name,
      description: product.description || product.name,
      category: product.category,
      url: `${siteUrl}/product/${id}`,
      offers: {
        '@type': 'Offer',
        availability: 'https://schema.org/InStock',
        priceCurrency: 'KRW',
        seller: {
          '@type': 'Organization',
          name: '한국 B2B 도매 카탈로그',
        },
      },
    };
  }, [product, id]);

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-white">
        <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full">
          <i className="ri-search-line text-3xl text-gray-400"></i>
        </div>
        <h2 className="text-xl font-black text-gray-900">找不到商品</h2>
        <p className="text-sm text-gray-400 tracking-widest">상품을 찾을 수 없습니다</p>
        <p className="text-sm text-gray-500">您请求的商品信息不存在。</p>
        <button
          onClick={() => navigate('/')}
          className="mt-2 px-6 py-3 bg-black text-white font-bold text-sm rounded-xl hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
        >
          返回首页 <span className="font-normal opacity-70">/ 홈으로 돌아가기</span>
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
      <SeoHead
        title={`${product.name} | 한국 B2B 도매 카탈로그`}
        description={`${product.name} - ${product.category} 카테고리 B2B 도매 공급 상품. 최저가 도매가로 공급받으세요.`}
        keywords={`${product.name}, ${product.category} 도매, 한국 B2B 도매`}
        canonical={`${siteUrl}/product/${id}`}
        jsonLd={productJsonLd ?? undefined}
      />
      <Header forceScrolled />

      {/* 헤더 높이만큼 여백 (헤더 2단: 상단바 64px + 카테고리바 ~44px = 108px + 배너) */}
      <div style={{ height: 'calc(108px + var(--banner-height, 0px))' }} />

      {/* Breadcrumb */}
      <div className="px-4 md:px-8 lg:px-12 py-4 border-b border-gray-100 bg-white">
        <div className="max-w-screen-xl mx-auto flex items-center gap-2 text-xs text-gray-400">
          <button
            onClick={() => navigate('/')}
            className="hover:text-black transition-colors cursor-pointer whitespace-nowrap"
          >
            首页 <span className="opacity-60">/ 홈</span>
          </button>
          <i className="ri-arrow-right-s-line"></i>
          <button
            onClick={() => {
              navigate('/');
              setTimeout(() => {
                const el = document.getElementById('categories');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }, 300);
            }}
            className="hover:text-black transition-colors cursor-pointer whitespace-nowrap"
          >
            {product.category}
          </button>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-gray-600 font-medium line-clamp-1">{product.name}</span>
        </div>
      </div>

      {/* Back Button */}
      <div className="px-4 md:px-8 lg:px-12 pt-6 pb-2">
        <div className="max-w-screen-xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-black transition-colors cursor-pointer group"
          >
            <div className="w-7 h-7 flex items-center justify-center border border-gray-200 rounded-full group-hover:border-black group-hover:bg-black group-hover:text-white transition-all">
              <i className="ri-arrow-left-s-line text-base"></i>
            </div>
            返回上页 <span className="opacity-60 text-xs">/ 이전으로</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <main className="px-4 md:px-8 lg:px-12 py-8">
        <div className="max-w-screen-xl mx-auto">
          <ProductDetailContent product={product} />
          <RelatedProducts products={related} currentId={product.id} />
        </div>
      </main>

      {/* Bottom CTA Banner */}
      <div className="bg-gray-50 border-t border-gray-200 px-4 md:px-8 lg:px-12 py-10">
        <div className="max-w-screen-xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-xl font-black text-gray-900 mb-1">大量订购 &amp; 追加商品咨询</h3>
            <p className="text-sm text-gray-400 tracking-widest">대량 주문 &amp; 추가 상품 문의</p>
            <p className="text-sm text-gray-500 mt-1">负责人将在工作日1~2天内与您联系。</p>
            <p className="text-xs text-gray-400 tracking-widest">담당자가 영업일 기준 1~2일 이내 연락드립니다.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <a
              href="tel:0230001234"
              className="px-6 py-3 border-2 border-black text-black font-bold text-sm rounded-xl hover:bg-black hover:text-white transition-all whitespace-nowrap cursor-pointer flex items-center gap-2"
            >
              <i className="ri-phone-line"></i>02-300-1234
            </a>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-black text-white font-bold text-sm rounded-xl hover:bg-gray-800 transition-all whitespace-nowrap cursor-pointer flex items-center gap-2"
            >
              <i className="ri-arrow-left-line"></i>
              查看全部目录 <span className="font-normal opacity-70 text-xs">/ 전체 카탈로그 보기</span>
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
